import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch } from 'react-icons/md';
import CollegeService from '../services/collegeService';
import CollegeCard from './CollegeCard';
import CollegeCardSkeleton from './CollegeCardSkeleton';
import { getCollegeId, getCollegeName, getCollegeSlug } from '../utils/collegeListing';

const PAGE_SIZE = 12;
const SKELETON_COUNT = 6;

const CollegeListSection = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const requestKeyRef = useRef('');
  const [colleges, setColleges] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const loadColleges = useCallback(async ({ nextPage, searchTerm, replace = false }) => {
    const requestKey = `${searchTerm}::${nextPage}`;

    if (requestKeyRef.current === requestKey) {
      return;
    }

    requestKeyRef.current = requestKey;
    setError('');

    if (replace) {
      setIsInitialLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await CollegeService.getCollegeListings({
        page: nextPage,
        limit: PAGE_SIZE,
        search: searchTerm
      });

      const validColleges = (response.data || []).filter((college) => {
        return Boolean(getCollegeId(college)) && Boolean(getCollegeName(college)?.trim());
      });

      setColleges((currentColleges) => {
        if (replace) {
          return validColleges;
        }

        const existingIds = new Set(currentColleges.map((college) => getCollegeId(college)));
        const merged = [...currentColleges];

        validColleges.forEach((college) => {
          const collegeId = getCollegeId(college);
          if (!existingIds.has(collegeId)) {
            existingIds.add(collegeId);
            merged.push(college);
          }
        });

        return merged;
      });

      setPage(response.page);
      setHasMore(response.hasMore);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load colleges right now.');
    } finally {
      requestKeyRef.current = '';
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setColleges([]);
    setPage(1);
    setHasMore(true);
    loadColleges({ nextPage: 1, searchTerm: debouncedSearch, replace: true });
  }, [debouncedSearch, loadColleges]);

  useEffect(() => {
    if (!loaderRef.current) {
      return undefined;
    }

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || isInitialLoading || isLoadingMore || !hasMore) {
          return;
        }

        loadColleges({ nextPage: page + 1, searchTerm: debouncedSearch });
      },
      {
        rootMargin: '220px 0px'
      }
    );

    observerRef.current.observe(loaderRef.current);

    return () => observerRef.current?.disconnect();
  }, [debouncedSearch, hasMore, isInitialLoading, isLoadingMore, loadColleges, page]);

  const handleViewProfile = useCallback((college) => {
    const collegeId = getCollegeId(college);
    const slug = getCollegeSlug(college);
    const targetPath = slug ? `/college-profile/${slug}` : '/college-profile';
    const search = collegeId ? `?collegeId=${encodeURIComponent(collegeId)}` : '';

    navigate(`${targetPath}${search}`);
  }, [navigate]);

  const emptyMessage = useMemo(() => {
    if (!debouncedSearch) {
      return 'No colleges are available right now.';
    }

    return `No colleges matched "${debouncedSearch}".`;
  }, [debouncedSearch]);

  return (
    <section className="mt-8 sm:mt-10 lg:mt-12">
      <div className="overflow-hidden rounded-[28px] border border-white/70 bg-gradient-to-br from-white via-white to-orange-50 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.35)]">
        <div className="border-b border-orange-100 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.14),_transparent_36%),radial-gradient(circle_at_top_right,_rgba(243,111,33,0.16),_transparent_34%)] px-5 py-6 sm:px-7 sm:py-7">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#9a51a0]">
                Explore Colleges
              </p>
              <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                Discover Top Colleges in India
              </h2>
              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                Explore detailed college profiles, compare rankings, placements, fees, and find the best college for your future.
              </p>
            </div>

            <div className="w-full max-w-lg">
              <label htmlFor="dashboard-college-search" className="mb-2 block text-sm font-medium text-slate-700">
                Search colleges
              </label>
              <div className="relative">
                <MdSearch className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  id="dashboard-college-search"
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search by college name, short name, state, city, or location"
                  className="w-full rounded-2xl border border-orange-100 bg-white/90 py-3 pl-12 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-[#9a51a0] focus:ring-2 focus:ring-purple-100"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-6 sm:px-7 sm:py-7">
          {error ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {isInitialLoading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <CollegeCardSkeleton key={`college-skeleton-${index}`} />
              ))}
            </div>
          ) : colleges.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-slate-600">
              {emptyMessage}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {colleges.map((college) => (
                  <CollegeCard
                    key={getCollegeId(college)}
                    college={college}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>

              {isLoadingMore ? (
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <CollegeCardSkeleton key={`college-loading-more-${index}`} />
                  ))}
                </div>
              ) : null}

              <div ref={loaderRef} className="h-8 w-full" />

              {!hasMore ? (
                <p className="pt-2 text-center text-sm text-slate-500">
                  You&apos;ve reached the end of the list.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CollegeListSection;
